import { useEffect, useRef, useState } from 'react'
import {
  Plus,
  Upload,
  Info,
  Check,
  LockOpen,
  PackageCheck,
  Boxes,
  CalendarClock,
  ClipboardCheck,
  ClipboardList,
  ClipboardPlus,
  FileText,
  FlaskConical,
  UploadCloud,
  PackagePlus,
  Scissors,
  Merge,
  PackageOpen,
  Warehouse,
  Truck,
  Beaker,
  Hourglass,
  Send,
  SkipForward,
  Undo2,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { WorkingProductPicker, type WorkingProductChoice } from '@/components/procedure/WorkingProductPicker'
import {
  PRODUCT_ACTION,
  formActionLabel,
  productOperationsFor,
  SHIP_PRODUCT_OPERATION,
  ACTION_ITEMS_ACTION,
  APPLY_TEST_RESULTS_ACTION,
} from '@/lib/formActions'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { fieldLibraryApi } from '@/api/fieldLibraryApi'
import { parseFormWorkbook, type ParsedForm } from '@/lib/formExcel'
import { EXCEL_ONLY_MESSAGE, isExcelFileName } from '@/lib/uploadedForm'

// ─── Global action menu ──────────────────────────────────────────────────────
// RBAC governs which of these are visible per role — wiring is UI-only for now.

interface ActionItem {
  label: string
  icon: React.ElementType
}

// "Create Product" is deliberately NOT in this list — per the Create New
// Product spec it gets its own always-visible "P" icon (below), not a menu
// item buried behind "+". Every other configured action stays in the menu.
const ORDER_TEST_ACTION = 'Order Test Results'
/** Shown wherever Order Test Results is; handled by the page's onAction. */
export const SEND_TO_EPIC_ACTION = 'Send to Epic'

const ACTIONS: ActionItem[] = [
  { label: 'Add Form', icon: FileText },
  { label: 'Reserve Equipment', icon: PackageCheck },
  { label: 'Reserve Inventory', icon: Boxes },
  { label: 'Reserve Space', icon: CalendarClock },
  { label: 'Create CAPA', icon: ClipboardList },
  { label: ACTION_ITEMS_ACTION, icon: ClipboardPlus },
  { label: 'Order Test Results', icon: FlaskConical },
  { label: APPLY_TEST_RESULTS_ACTION, icon: ClipboardCheck },
]

/**
 * The "+" menu for a form's configured actions. Send to Epic rides with Order
 * Test Results rather than being its own configured action: a form that orders
 * tests is the one whose results go back to Epic (z8nrz7ffzv / z8nrz7fgcp), and
 * no form needs re-authoring to get it.
 */
export function menuActions(allowedActions: readonly string[] | null | undefined): ActionItem[] {
  if (allowedActions == null) return []
  return ACTIONS.filter(a => allowedActions.includes(a.label)).flatMap(a =>
    a.label === ORDER_TEST_ACTION ? [a, { label: SEND_TO_EPIC_ACTION, icon: Send }] : [a],
  )
}

/**
 * The icon for each product operation, keyed by its PERSISTED value.
 *
 * Keyed on the value and not the label for the reason the dashboard nav learned
 * the hard way: renaming a label there orphaned its icon. The labels and the
 * `ready` flag live with the operations themselves in `formActions.ts`, which
 * the builder's checklist reads too — one list, so the menu can only ever offer
 * what a form was configured with.
 *
 * `ready: false` entries render as a disabled <button>, which browsers remove
 * from the tab order — keyboard users skip them rather than landing on
 * something that does nothing.
 */
const PRODUCT_OPERATION_ICONS: Record<string, React.ElementType> = {
  'Product: Create new product': PackagePlus,
  'Product: Split product': Scissors,
  'Product: Pool / combine product': Merge,
  'Product: Receive product': PackageOpen,
  'Product: Update product': Beaker,
  'Product: Store product': Warehouse,
  'Product: Ship product': Truck,
}

/** Closes the popover when clicking outside of it. */
function useClickOutside<T extends HTMLElement>(onOutside: () => void) {
  const ref = useRef<T>(null)
  useEffect(() => {
    function handle(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) onOutside()
    }
    document.addEventListener('mousedown', handle)
    return () => document.removeEventListener('mousedown', handle)
  }, [onOutside])
  return ref
}

function ActionIcon({
  label,
  active,
  onClick,
  children,
}: {
  label: string
  active?: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type='button'
      title={label}
      aria-label={label}
      onClick={onClick}
      className={cn(
        'flex h-8 w-8 items-center justify-center rounded-full border transition-colors',
        active
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-transparent bg-primary/10 text-primary hover:bg-primary/20',
      )}
    >
      {children}
    </button>
  )
}

export function FormActionBar({
  formName,
  completed = false,
  pendingReview = false,
  allowedActions,
  rightSlot,
  onAction,
  onCreateProduct,
  onProductOperation,
  onDisplayPatientHistory,
  patientHistoryCount,
  patientName,
  productCount,
  productLabel,
  onManageProducts,
  workingProducts,
  onUnlock,
  skipped = false,
  onSkip,
  onUnskip,
  canShipProducts = false,
  onUploadForm,
  actionItemCount,
  onOpenActionItems,
}: {
  formName?: string
  /**
   * Saves a form read from an uploaded workbook and attaches it to this
   * procedure (z8nrz7f030). Without it the dialog can take a file but not
   * upload it.
   */
  onUploadForm?: (form: ParsedForm) => Promise<void>
  /** Whether the user holds products.ship; hides the Ship product operation otherwise. */
  canShipProducts?: boolean
  completed?: boolean
  /**
   * Filled in and waiting for its review signature (z8nrz7dt41). Closed like a
   * completed form — Unlock applies and withdraws the review request — but
   * labelled as what it is, since it is not done.
   */
  pendingReview?: boolean
  /**
   * Fired by the Unlock button beside the Completed pill. Pass it only when the
   * user may unlock: without it the button is not rendered at all.
   */
  onUnlock?: () => void
  /** Left unfilled on purpose: read-only, and done for the run. */
  skipped?: boolean
  /**
   * "+" → Skip Form: leave this form unfilled and move on to the next. Pass it
   * only when the form may be skipped: its definition offers the "Skip Form"
   * action, it is not done yet, and the user may.
   */
  onSkip?: () => void
  /** "+" → Unskip Form, on a skipped form: open it to be filled after all. */
  onUnskip?: () => void
  allowedActions?: string[]
  /** Extra controls pinned to the right end of the bar (e.g. Populate from instrument). */
  rightSlot?: React.ReactNode
  onAction?: (label: string) => void
  /** Fired by the standalone "P" icon — shown whenever `allowedActions` includes 'Create Product'. */
  onCreateProduct?: () => void
  /**
   * Fired with the clicked operation's PERSISTED value (e.g. 'Product: Receive
   * product'). When provided it takes over from `onCreateProduct`, which
   * remains as the every-operation-opens-Create fallback.
   */
  onProductOperation?: (value: string) => void
  /** Fired by the standalone "H" icon — shown whenever `allowedActions` includes 'Display Patient History'. */
  onDisplayPatientHistory?: () => void
  /** Number of other procedures for the same patient, shown as a badge on the H icon. */
  patientHistoryCount?: number
  /**
   * The patient, shown with the form name. With `productLabel` it makes the bar
   * a procedure label: form, patient and product.
   */
  patientName?: string | null
  /**
   * How many main products the procedure has (split aliquots not counted),
   * shown as a count beside the "P" icon. Procedure-wide so it reads the same
   * on every form. The count is its own button — clicking it opens the
   * Products dialog, while "P" itself still opens the operations menu, so
   * neither action is buried behind the other.
   */
  productCount?: number
  /**
   * Those main products, shown just before "P": the first as "DIN / code" and
   * "…" for the rest (`text`), all of them on hover (`title`).
   */
  productLabel?: { text: string; title: string }
  /** Fired by that count. Without it the count renders as plain text, not a control. */
  onManageProducts?: () => void
  /**
   * Which product(s) this form is about (z8nrz7fxub). With more than one choice
   * the "P" menu opens with a "Working on" picker; while `needsChoice`, "P" is
   * red and the menu opens by itself once per form (`formKey`).
   */
  workingProducts?: {
    choices: WorkingProductChoice[]
    selectedIds: string[]
    needsChoice: boolean
    formKey: string
    onSave: (ids: string[]) => Promise<void>
  }
  /**
   * This form's action items not yet completed, shown as "Action (n)" whenever
   * `allowedActions` includes 'Action Items' (z8nrz7fkfh).
   */
  actionItemCount?: number
  /** Fired by the Action (n) badge: the Action Items list, on this form. */
  onOpenActionItems?: () => void
}) {
  const [menuOpen, setMenuOpen] = useState(false)
  const [infoOpen, setInfoOpen] = useState(false)
  const [uploadOpen, setUploadOpen] = useState(false)
  const [productMenuOpen, setProductMenuOpen] = useState(false)

  const menuRef = useClickOutside<HTMLDivElement>(() => setMenuOpen(false))
  const infoRef = useClickOutside<HTMLDivElement>(() => setInfoOpen(false))
  const productRef = useClickOutside<HTMLDivElement>(() => setProductMenuOpen(false))

  const visibleActions = menuActions(allowedActions)
  // The stored action value is 'Create Product'; what a user reads is the
  // display override. Never compare against the display name.
  const showCreateProduct = allowedActions?.includes(PRODUCT_ACTION) ?? false
  const productActionName = formActionLabel(PRODUCT_ACTION)
  /**
   * Only what this form was configured to offer.
   *
   * A form saved before operations could be chosen has none recorded and gets
   * the whole menu, exactly as it does today — see `productOperationsFor`.
   */
  const productOperations = productOperationsFor(allowedActions).filter(
    // The form may offer Ship product, but this USER may not hold products.ship
    // (z8nrz7eaex): the menu would open a dialog whose signed POST then 403s.
    o => o.value !== SHIP_PRODUCT_OPERATION || canShipProducts,
  )
  const showPatientHistory = allowedActions?.includes('Display Patient History') ?? false
  // The product part of the label shows on every form that has a product to
  // name, whether or not this form offers the product operations.
  const showPicker = (workingProducts?.choices.length ?? 0) > 1
  const needsChoice = showPicker && !!workingProducts?.needsChoice
  const showProducts = showCreateProduct || showPicker || (productCount ?? 0) > 0

  // Ask once per form: open the picker by itself the first time a form needs a
  // choice. Closing it leaves "P" red until one is saved.
  const askedFor = useRef<Set<string>>(new Set())
  const formKey = workingProducts?.formKey
  useEffect(() => {
    if (!needsChoice || !formKey || askedFor.current.has(formKey)) return
    askedFor.current.add(formKey)
    setProductMenuOpen(true)
  }, [needsChoice, formKey])
  const showActionItems = (allowedActions?.includes(ACTION_ITEMS_ACTION) ?? false) && !!onOpenActionItems

  return (
    <div className='flex items-center gap-2 border-b bg-muted/20 px-4 py-1.5'>
      {/* Global "+" action menu: the form's configured actions, and Skip/Unskip */}
      {(visibleActions.length > 0 || onSkip || onUnskip) && (
        <div className='relative' ref={menuRef}>
          <ActionIcon
            label='Actions'
            active={menuOpen}
            onClick={() => {
              setInfoOpen(false)
              setMenuOpen(o => !o)
            }}
          >
            <Plus className='h-4 w-4' />
          </ActionIcon>
          {menuOpen && (
            <div className='absolute left-0 top-10 z-30 w-56 overflow-hidden rounded-lg border bg-popover shadow-lg'>
              <p className='border-b px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                Actions
              </p>
              <ul className='py-1'>
                {visibleActions.map(({ label, icon: Icon }) => (
                  <li key={label}>
                    <button
                      type='button'
                      onClick={() => {
                        setMenuOpen(false)
                        onAction?.(label)
                      }}
                      className='flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground'
                    >
                      <Icon className='h-4 w-4 text-muted-foreground' />
                      {label}
                    </button>
                  </li>
                ))}
                {(onSkip || onUnskip) && (
                  <li className={cn(visibleActions.length > 0 && 'mt-1 border-t pt-1')}>
                    <button
                      type='button'
                      onClick={() => {
                        setMenuOpen(false)
                        if (onSkip) onSkip()
                        else onUnskip?.()
                      }}
                      className='flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm hover:bg-accent hover:text-accent-foreground'
                    >
                      {onSkip ? (
                        <SkipForward className='h-4 w-4 text-muted-foreground' />
                      ) : (
                        <Undo2 className='h-4 w-4 text-muted-foreground' />
                      )}
                      {onSkip ? 'Skip Form' : 'Unskip Form'}
                    </button>
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Upload */}
      <ActionIcon label='Bulk upload forms (Excel)' onClick={() => setUploadOpen(true)}>
        <Upload className='h-4 w-4' />
      </ActionIcon>

      {/* Info / instructions */}
      <div className='relative' ref={infoRef}>
        <ActionIcon
          label='Instructions'
          active={infoOpen}
          onClick={() => {
            setMenuOpen(false)
            setInfoOpen(o => !o)
          }}
        >
          <Info className='h-4 w-4' />
        </ActionIcon>
        {infoOpen && (
          <div className='absolute left-0 top-10 z-30 w-96 overflow-hidden rounded-xl border bg-popover shadow-xl'>
            <div className='border-b px-4 py-2.5'>
              <p className='text-sm font-semibold'>Instructions</p>
            </div>
            <img
              src='/images/title21-computer.jpeg'
              alt={formName ? `${formName} reference` : 'Reference'}
              className='max-h-64 w-full object-cover'
            />
            <p className='px-4 py-3 text-sm text-muted-foreground'>
              Reference for completing {formName ? <span className='font-medium text-foreground'>{formName}</span> : 'this form'}.
              Use the <span className='font-medium text-foreground'>+</span> menu to run actions, or the
              upload icon to import data from an Excel file.
            </p>
          </div>
        )}
      </div>

      {/* Form title lives in the bar so the form area needs no separate header row. */}
      {formName && (
        <>
          <div className='mx-1 h-5 w-px shrink-0 bg-border' />
          <h2 className='min-w-0 truncate text-sm font-semibold' title={formName}>
            {formName}
          </h2>
          {patientName && (
            <span className='min-w-0 truncate text-sm text-muted-foreground' title={patientName}>
              · {patientName}
            </span>
          )}
        </>
      )}

      {completed && (
        <span className='flex shrink-0 items-center gap-1 rounded-full border border-green-200 bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700'>
          <Check className='h-3 w-3 shrink-0' />
          Completed
        </span>
      )}

      {skipped && (
        <span className='flex shrink-0 items-center gap-1 rounded-full border border-slate-300 bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600'>
          <SkipForward className='h-3 w-3 shrink-0' />
          Skipped
        </span>
      )}

      {pendingReview && (
        <span className='flex shrink-0 items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700'>
          <Hourglass className='h-3 w-3 shrink-0' />
          Pending review
        </span>
      )}

      {(completed || pendingReview) && onUnlock && (
        <button
          type='button'
          title='Unlock this form'
          aria-label='Unlock this form'
          onClick={onUnlock}
          className='flex shrink-0 items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 hover:bg-amber-100'
        >
          <LockOpen className='h-3 w-3 shrink-0' />
          Unlock
        </button>
      )}

      {(showActionItems || showPatientHistory || showProducts || rightSlot) && (
        <div className='ml-auto flex shrink-0 items-center gap-2'>
          {showActionItems && (
            <button
              type='button'
              title='Action items on this form'
              aria-label={`Action items on this form (${actionItemCount ?? 0} open)`}
              onClick={onOpenActionItems}
              className='flex h-8 shrink-0 items-center justify-center rounded-full px-1.5 text-sm font-bold text-primary hover:bg-primary/10'
            >
              Action
              <span className='ml-1 text-xs font-semibold'>({actionItemCount ?? 0})</span>
            </button>
          )}
          {showProducts && (
            <div className='relative flex min-w-0 items-center' ref={productRef}>
              {/* The procedure's main products, right beside "P": the first,
                  "…" for the rest, and all of them on hover. */}
              {productLabel?.text && (
                <span
                  className='mr-1 max-w-[18rem] truncate text-sm font-semibold'
                  title={productLabel.title}
                  data-testid='procedure-product-label'
                >
                  {productLabel.text}
                </span>
              )}
              {(showCreateProduct || showPicker) && (
              <button
                type='button'
                title={needsChoice ? 'Choose the product you are working on' : productActionName}
                aria-label={needsChoice ? `${productActionName}: choose the product you are working on` : productActionName}
                aria-haspopup='menu'
                aria-expanded={productMenuOpen}
                onClick={() => {
                  setMenuOpen(false)
                  setInfoOpen(false)
                  setProductMenuOpen(o => !o)
                }}
                data-needs-choice={needsChoice || undefined}
                className={cn(
                  'flex h-8 w-8 items-center justify-center rounded-full text-base font-bold hover:bg-primary/10',
                  // Red while a product must be chosen, blue again once it is.
                  needsChoice ? 'bg-destructive/10 text-destructive ring-2 ring-destructive/40' : 'text-primary',
                  productMenuOpen && !needsChoice && 'bg-primary/10',
                )}
              >
                P
              </button>
              )}
              {/* Sibling button, not nested inside "P": one control per action,
                  and a <button> inside a <button> is invalid markup. */}
              {productCount != null && productCount > 0 && (
                <button
                  type='button'
                  title='Products in this procedure'
                  aria-label={`Products in this procedure (${productCount})`}
                  onClick={() => {
                    setProductMenuOpen(false)
                    onManageProducts?.()
                  }}
                  disabled={!onManageProducts}
                  className='shrink-0 pr-0.5 text-xs font-semibold text-primary hover:underline disabled:no-underline'
                >
                  {showCreateProduct ? `(${productCount})` : `P (${productCount})`}
                </button>
              )}
              {productMenuOpen && (
                // Right-aligned: this icon sits at the right end of the bar, and
                // a left-aligned panel would run off the edge.
                <div className='absolute right-0 top-10 z-30 w-72 overflow-hidden rounded-lg border bg-popover shadow-lg'>
                  {showPicker && workingProducts && (
                    <WorkingProductPicker
                      // Re-keyed per form, so the draft starts from that form's choice.
                      key={workingProducts.formKey}
                      choices={workingProducts.choices}
                      selectedIds={workingProducts.selectedIds}
                      needsChoice={needsChoice}
                      onSave={async ids => {
                        await workingProducts.onSave(ids)
                        setProductMenuOpen(false)
                      }}
                    />
                  )}
                  {showCreateProduct && (
                  <>
                  <p className='border-b px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                    {productActionName}
                  </p>
                  <ul className='py-1'>
                    {productOperations.map(({ value, label, ready }) => {
                      const Icon = PRODUCT_OPERATION_ICONS[value] ?? PackagePlus
                      return (
                      <li key={value}>
                        <button
                          type='button'
                          disabled={!ready}
                          onClick={
                            ready
                              ? () => {
                                  // Closing first: the flow opens a dialog, and
                                  // leaving the menu behind it looks like a
                                  // second click is still pending.
                                  setProductMenuOpen(false)
                                  if (onProductOperation) onProductOperation(value)
                                  else onCreateProduct?.()
                                }
                              : undefined
                          }
                          className={cn(
                            'flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm',
                            ready
                              ? 'hover:bg-accent hover:text-accent-foreground'
                              : 'cursor-not-allowed text-muted-foreground',
                          )}
                        >
                          <Icon className='h-4 w-4 shrink-0 text-muted-foreground' />
                          <span className='flex-1 whitespace-nowrap'>{label}</span>
                          {!ready && (
                            <span className='shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide'>
                              Soon
                            </span>
                          )}
                        </button>
                      </li>
                      )
                    })}
                  </ul>
                  </>
                  )}
                </div>
              )}
            </div>
          )}
          {showPatientHistory && (
            <button
              type='button'
              title='Display Patient History'
              aria-label='Display Patient History'
              onClick={() => onDisplayPatientHistory?.()}
              className='flex h-8 shrink-0 items-center justify-center rounded-full px-1.5 text-base font-bold text-primary hover:bg-primary/10'
            >
              H
              {patientHistoryCount != null && patientHistoryCount > 0 && (
                <span className='ml-0.5 text-xs font-semibold'>
                  ({patientHistoryCount})
                </span>
              )}
            </button>
          )}
          {rightSlot}
        </div>
      )}

      <UploadFormsDialog open={uploadOpen} onOpenChange={setUploadOpen} onUpload={onUploadForm} />
    </div>
  )
}

// ─── Upload dialog ───────────────────────────────────────────────────────────
// Bulk form authoring from an Excel workbook, reached from the procedure
// toolbar (z8nrz7f030). The workbook is read with the form builder's own
// parser; a valid one is saved and attached to THIS procedure as an ad-hoc
// form (what Add Form does), so it appears in the sidebar ready to fill. The
// saving and attaching belong to the page, which owns the procedure.

function UploadFormsDialog({
  open,
  onOpenChange,
  onUpload,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  onUpload?: (form: ParsedForm) => Promise<void>
}) {
  const [file, setFile] = useState<File | null>(null)
  const [refused, setRefused] = useState<string | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [problems, setProblems] = useState<{ errors: string[]; missingFields: string[] } | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const reset = () => {
    setFile(null)
    setRefused(null)
    setDragOver(false)
    setBusy(null)
    setProblems(null)
    if (inputRef.current) inputRef.current.value = ''
  }

  // While the dialog is open a file dropped anywhere — on the overlay, outside
  // the drop zone — must never make the browser open or download it (AC2).
  useEffect(() => {
    if (!open) return
    const block = (e: DragEvent) => e.preventDefault()
    window.addEventListener('dragover', block)
    window.addEventListener('drop', block)
    return () => {
      window.removeEventListener('dragover', block)
      window.removeEventListener('drop', block)
    }
  }, [open])

  const choose = (picked: File | undefined) => {
    setProblems(null)
    if (!picked) return
    if (!isExcelFileName(picked.name)) {
      setFile(null)
      setRefused(EXCEL_ONLY_MESSAGE)
      return
    }
    setRefused(null)
    setFile(picked)
  }

  const upload = async () => {
    if (!file || !onUpload) return
    setProblems(null)
    setBusy('Reading the workbook…')
    try {
      const fields = await fieldLibraryApi.listAllFields()
      const res = await parseFormWorkbook(await file.arrayBuffer(), fields)
      if (!res.ok || !res.form) {
        setProblems({ errors: res.errors, missingFields: res.missingFieldNames })
        return
      }
      setBusy('Adding the form to this procedure…')
      await onUpload(res.form)
      reset()
      onOpenChange(false)
    } catch (e) {
      setProblems({
        errors: [e instanceof Error ? e.message : 'Failed to upload the form.'],
        missingFields: [],
      })
    } finally {
      setBusy(null)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={o => {
        if (busy) return
        if (!o) reset()
        onOpenChange(o)
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload forms</DialogTitle>
          <DialogDescription>
            Bulk upload forms from an Excel file. The form is added to this procedure, ready to fill.
          </DialogDescription>
        </DialogHeader>

        <button
          type='button'
          onClick={() => inputRef.current?.click()}
          onDragEnter={e => {
            e.preventDefault()
            setDragOver(true)
          }}
          onDragOver={e => {
            e.preventDefault()
            e.dataTransfer.dropEffect = 'copy'
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => {
            e.preventDefault()
            setDragOver(false)
            choose(e.dataTransfer.files?.[0])
          }}
          disabled={!!busy}
          className={cn(
            'flex w-full flex-col items-center justify-center gap-2 rounded-lg border border-dashed bg-muted/20 px-6 py-10 text-center transition-colors hover:bg-muted/40',
            dragOver && 'border-primary bg-primary/5',
          )}
        >
          <UploadCloud className='h-8 w-8 text-muted-foreground' />
          <p className='text-sm font-medium'>{file?.name ?? 'Drag file here or click to upload'}</p>
          <p className='text-xs text-muted-foreground'>Excel (.xlsx, .xls) — bulk upload forms</p>
        </button>
        <input
          ref={inputRef}
          type='file'
          accept='.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel'
          className='hidden'
          onChange={e => {
            choose(e.target.files?.[0])
            // Cleared so picking the same file again still fires a change.
            e.target.value = ''
          }}
        />
        {refused && (
          <p role='alert' className='text-xs text-destructive'>
            {refused}
          </p>
        )}
        {busy && (
          <p role='status' className='text-xs text-muted-foreground'>
            {busy}
          </p>
        )}
        {problems && (
          <div
            role='alert'
            className='max-h-48 space-y-1 overflow-y-auto rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive'
          >
            {problems.missingFields.length > 0 && (
              <p>Not in the Field Library — create these there first: {problems.missingFields.join(', ')}</p>
            )}
            {problems.errors.map((err, i) => (
              <p key={i}>{err}</p>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button
            type='button'
            variant='outline'
            disabled={!!busy}
            onClick={() => {
              reset()
              onOpenChange(false)
            }}
          >
            Cancel
          </Button>
          <Button type='button' disabled={!file || !onUpload || !!busy} onClick={() => void upload()}>
            {busy ? 'Uploading…' : 'Upload'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
